import React, {useEffect, useRef, useState} from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import ViewShot from 'react-native-view-shot';
import {RHButton, RHSeparator} from 'src/common-components/custom-ui-helpers';
import {Menu, Modal} from 'src/design-system';
import {Colors, Typography} from 'src/styles';
import SessionSummaryDataTable from 'src/modules/session/components/summary-data-table';
import {SessionMessage} from 'src/modules/session/components';
import {useNavigation} from '@react-navigation/core';
import {Separator, Success, Error} from 'src/common-components/atoms';
import {FormProvider, useForm, useWatch} from 'react-hook-form';
import moment from 'moment';
import {compose} from 'recompose';
import {withDatabase} from '@nozbe/watermelondb/react';
import {withObservables} from '@nozbe/watermelondb/react';
import withState from 'src/redux/wrapper';
import {exportCsv} from 'src/common-utils/export-csv';
import {exportGraph} from 'src/common-utils/export-graph';
import {useStyle} from 'src/providers/style';
import {useTranslations} from 'src/providers/translation';
import SessionInfoDisplay from 'src/modules/organization/components/session-info-display';
import ClientInfoDisplay from 'src/modules/organization/components/client-info-display';
import OrganizationDisplay from 'src/modules/organization/components/organization-display';
import {mergeMap, of} from 'rxjs';
import {Q} from '@nozbe/watermelondb';
import RenderFormInputs from 'src/modules/form-builder/components/render-form-inputs';
import {
  Instance,
  InstanceDiagnosis,
  Note,
  NoteTemplateVersion,
  Session,
  User,
} from 'src/models';
import {Display as SignatureDisplay} from 'src/modules/session/components/signature-item';
import SessionNoteListItem from '../session-note-list-item';
import {map} from '@nozbe/watermelondb/utils/rx';
import {SYNC_SERVER} from 'react-native-dotenv';
import AsyncStorage from '@react-native-async-storage/async-storage';
import _ from 'lodash';
import HookFormInput from 'src/hook-form-wrapper/form-input';
import * as Yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {useSelector} from 'react-redux';
import Collapsible from 'react-native-collapsible';
import {IconButton} from 'react-native-paper';
import AmendedDataTableRow, {
  friendlyFieldName,
  VISIBLE_FIELDS,
} from 'src/modules/session/components/amended-data-table-row';
import {useDatabase} from '@nozbe/watermelondb/hooks';
import Bugsnag from 'src/common-utils/bugsnag-set-user';
import {useDimensions} from '@react-native-community/hooks';

const isWeb = Platform.OS === 'web';

const SessionRecap = ({
  session,
  appointment,
  patient,
  staff,
  // canEdit = false,
  isSupervision = false,
  diagnoses,
  sessionHistory = false,
  noteTemplate,
  careTeam,
  organization,
  noteTemplateValue,
  signatures,
  notes,
  billingCodes,
  supervisedSession,
  role,
}: any) => {
  const screenRef = useRef();
  const navigation = useNavigation();
  const styles = useStyle();
  const translations = useTranslations();
  const database = useDatabase();
  const dimensions = useDimensions();

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<any>(null);
  const [sessionSignatures, setSignatures] = useState(signatures);
  const [auditLogs, setAuditLogs] = useState([]);
  const {userId} = useSelector(state => state.authentication);

  const isSmallSize = dimensions.window.width <= 768;

  const methods = useForm({
    defaultValues: {
      note: session?.note,
      address: session?.address ?? '',
    },
  });

  const updateNote = async () => {
    await session.updateEntity({
      submissionTimestamp: new Date(), //?
    });
  };

  const watchedNote = useWatch({
    control: methods.control,
    name: 'note',
  });
  const [concattedNotes, setConcattedNotes] = useState<string>('');
  const [editMode, setEditMode] = useState<boolean>(false);
  const [showAmendModal, setShowAmendModal] = useState<boolean>(false);
  const [showModifiedData, setShowModifiedData] = useState<boolean>(false);

  const handleUpdatedSigs = (updatedSignatures: any) => {
    setSignatures(updatedSignatures);
  };

  useEffect(() => {
    methods.setValue('note', session?.note);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  const validationSchema = Yup.object({
    amendmentReason: Yup.string().required('Reason is Required'),
  });

  const amendmentForm = useForm({
    defaultValues: {
      amendmentReason: '',
    },
    resolver: yupResolver(validationSchema),
  });

  const submitAmendment = async (values: any) => {
    try {
      const token = await AsyncStorage.getItem('accessToken');
      const response = await fetch(
        `${SYNC_SERVER}/amend-session?sessionId=${session.id}&amending=true`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.warn('Failed to disable amend mode:', errorText);
        setToast({
          title: 'Could not complete amendment. Please try again.',
          message: '',
          success: false,
        });
        return;
      }

      await session.updateEntity({
        amendmentReason: values?.amendmentReason,
        amendedBy: userId,
        amendedAt: new Date(),
      });
      navigation.navigate('SessionCollect', {
        id: session?.id,
        amend: true,
      });
    } catch (error) {
      console.error('Error in completeAmendCallback:', error);
      setToast({
        success: false,
        title: 'Unexpected Error',
        message: '',
      });
    }
  };

  useEffect(() => {
    if (sessionSignatures.length === 0 || !editMode) {
      const fetchSessionNote = async () => {
        setLoading(true);
        try {
          const response = await fetch(
            `${SYNC_SERVER}/fetch-session-note?entityId=${session.id}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await AsyncStorage.getItem(
                  'accessToken',
                )}`,
              },
            },
          );
          if (response.status === 200) {
            const values = await response.json();
            if (
              values?.signatures?.length > 0 &&
              sessionSignatures.length === 0
            ) {
              setSignatures(values?.signatures);
            } else if (
              values?.signatures?.length > 0 &&
              sessionSignatures.length > 0
            ) {
              const sigArray = _.uniqWith(
                [...values?.signatures, ...sessionSignatures],
                (a, b) => a.id === b.id,
              );
              setSignatures(sigArray);
            }
            if (values?.notes?.length > 0 && notes?.length === 0) {
              const fetchedNotes = values?.notes;
              await database.write(async () => {
                const sessionNotes = database.get('notes');
                for (const item of fetchedNotes) {
                  await sessionNotes.create(note => {
                    note._raw.id = item.id; // required if you're inserting by ID
                    note.entity = item.entity;
                    note.entityId = item.entity_id;
                    note.partition = item._partition;
                    note.description = item.description;
                    note.createdAt = item.created_at;
                    note.createdBy = item.created_by;
                    note.updatedAt = item.updated_at;
                    note.updatedBy = item.updated_by;
                    note.deletedAt = item.deleted_at;
                    note.deletedBy = item.deleted_by;
                  });
                }
              });
            }
          }
        } catch (e) {
          Bugsnag.notify(`Session Recap Fetch Error: ${session.id} - ${e}`);
          console.log('fetch error:', e);
        } finally {
          setLoading(false);
        }
      };

      fetchSessionNote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  useEffect(() => {
    if (!editMode && session?.amendedAt > 0) {
      const fetchAuditLogs = async () => {
        setLoading(true);
        try {
          const response = await fetch(
            `${SYNC_SERVER}/audit-log?auditable_id=${session.id}&auditable_type=session`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await AsyncStorage.getItem(
                  'accessToken',
                )}`,
              },
            },
          );
          if (response.status === 200) {
            const values = await response.json();
            if (values) {
              setAuditLogs(values?.entities);
            }
          }
        } catch (e) {
          Bugsnag.notify(`Session Recap Fetch Error: ${session.id} - ${e}`);
          console.log('fetch error:', e);
        } finally {
          setLoading(false);
        }
      };
      fetchAuditLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  const concatNotes = (notesArr: Note[]) => {
    let resultStr = '';

    for (const note of notesArr) {
      resultStr += `${note?.description} \n`;
    }

    return resultStr;
  };

  useEffect(() => {
    setConcattedNotes(concatNotes(notes));
  }, [notes]);

  const exportPDF = async () => {
    const fileName = `${patient.firstName}_${
      patient.lastName
    }_session_export_${moment(session.date).format('L')}_${moment(
      session.date,
    ).format('LT')}`.replace(/[:/ ]/g, '_');
    setLoading(true);
    await exportGraph(screenRef, fileName, 'pdf');
    setLoading(false);
  };

  const createCSVFile = () => {
    let _exportData = [
      [
        'Client Name',
        'Staff Name',
        'Session Date',
        'Session Start Time',
        'Session Edited Start Time ',
        'Session End Time',
        'Session Edited End Time',
        'Session Note',
      ],
      [
        `${patient.firstName} ${patient.lastName}`,
        `${staff[0].firstName} ${staff[0].lastName}`,
        `${session.date}`,
        `${session.startTimestamp}`,
        `${session.editedStartTimestamp}`,
        `${session.endTimestamp}`,
        `${session.editedEndTimestamp}`,
        `${session.note} ${
          notes ? notes.map((note: any) => note?.description) : ''
        }`,
      ],
    ];
    exportCsv(
      `${patient.firstName}_${patient.lastName}_session_export_${moment(
        session.date,
      )
        .format('L')
        .split('/')
        .join('_')}`,
      _exportData,
    );
  };

  const options = [
    {
      onPress: createCSVFile,
      title: translations('csv'),
      icon: 'file-excel',
    },
  ];

  if (isWeb) {
    options.unshift({
      onPress: exportPDF,
      title: translations('pdf'),
      icon: 'file-pdf-box',
    });
  }

  return (
    <View>
      <View
        style={[
          styles.container,
          styles.justifyEnd,
          styles.marginTop,
          styles.marginHorizontal,
        ]}>
        {!sessionHistory ? (
          <>
            {session?.type !== 'supervision' && role?.amendedSessionCreate ? (
              <View style={[styles.paddingRight]}>
                <RHButton
                  mode="outlined"
                  secondary
                  textColor={Colors.RAVEN_BLACK}
                  onPress={() => {
                    setShowAmendModal(true);
                  }}>
                  <Icon name="undo" size={18} style={[styles.marginSMRight]} />
                  Amend Data
                </RHButton>
              </View>
            ) : null}
            <Modal
              show={[showAmendModal, setShowAmendModal]}
              title={'Amend Session Data'}>
              <FormProvider {...amendmentForm}>
                <View>
                  <Text style={[Typography.P3]}>
                    You are about to amend this session. Please provide an
                    amendment reason to continue.
                  </Text>
                  <View style={[styles.paddingTop]}>
                    <HookFormInput
                      name={'amendmentReason'}
                      required={true}
                      label={'Reason for Amendment'}
                    />
                  </View>
                  <View
                    style={[
                      styles.row,
                      styles.justifySpaceBetween,
                      styles.paddingTop,
                    ]}>
                    <RHButton
                      mode="outlined"
                      textColor={Colors.RAVEN_BLACK}
                      style={[]}
                      onPress={() => {
                        setShowAmendModal(false);
                        amendmentForm.setValue('amendmentReason', '');
                      }}>
                      Cancel
                    </RHButton>
                    <RHSeparator width={10} />
                    <RHButton
                      mode="contained"
                      onPress={() => {
                        amendmentForm.handleSubmit(async values => {
                          await submitAmendment(values);
                        })();
                      }}>
                      CONTINUE
                    </RHButton>
                  </View>
                </View>
              </FormProvider>
              {toast ? <Separator height={20} /> : <></>}
              {toast && toast.success ? (
                <Success title={toast.title} message={toast.message} />
              ) : toast && !toast.success ? (
                <Error title={toast.title} message={toast.message} />
              ) : (
                <></>
              )}
            </Modal>
            {role?.sessionEdit ? (
              <View style={[styles.paddingRight]}>
                {!editMode ? (
                  <RHButton
                    mode="outlined"
                    secondary
                    textColor={Colors.RAVEN_BLACK}
                    onPress={() => {
                      setEditMode(true);
                    }}>
                    <Icon
                      name="pencil"
                      size={18}
                      style={[styles.marginSMRight]}
                    />
                    Edit
                  </RHButton>
                ) : (
                  <>
                    <RHButton
                      mode="contained"
                      color={Colors.TERTIARY_TEAL}
                      secondary
                      onPress={() => setEditMode(false)}>
                      <Icon
                        name="content-save"
                        size={18}
                        style={[styles.marginSMRight]}
                      />
                      SAVE
                    </RHButton>
                  </>
                )}
              </View>
            ) : (
              <></>
            )}
            <Menu
              anchor={
                <RHButton
                  loading={loading}
                  secondary
                  textColor={Colors.RAVEN_BLACK}
                  mode="outlined">
                  <Icon
                    name={'export-variant'}
                    size={18}
                    style={[styles.marginSMRight]}
                  />
                  {translations('export_recap')}
                </RHButton>
              }
              options={options}
            />
            <Separator width={16} />
            <RHButton
              onPress={() => {
                methods.handleSubmit(updateNote);
                navigation.navigate('Dashboard');
              }}
              secondary
              textColor={Colors.RAVEN_BLACK}
              mode="outlined">
              <Icon name={'home'} size={18} style={[styles.marginSMRight]} />
              {translations('finalize_session')}
            </RHButton>
          </>
        ) : (
          <></>
        )}
      </View>
      {editMode ? (
        <SessionMessage
          session={session}
          showSubmitButton={false}
          fromRecap={true}
          recapSignatures={sessionSignatures}
          onUpdate={handleUpdatedSigs}
        />
      ) : (
        <ViewShot ref={screenRef}>
          <View
            style={[
              styles.width,
              styles.row,
              styles.justifySpaceBetween,
              styles.alignCenter,
            ]}>
            <Text style={[Typography.H4, styles.textColorPrimary]}>
              {isSupervision
                ? translations('supervision_summary')
                : translations('summary')}
            </Text>
          </View>
          <Text style={[Typography.H5, styles.marginTop]}>
            {translations('selected_template')}
          </Text>
          <View
            style={[
              styles.marginLVertical,
              styles.cardFlat,
              styles.padding,
              styles.borderRadius,
            ]}>
            <Text style={[Typography.P3]}>
              {noteTemplate?.title || translations('no_template_selected')}
            </Text>
          </View>
          {noteTemplate?.organizationInformation ? (
            <OrganizationDisplay
              currentNoteTemplate={noteTemplate}
              organization={organization}
            />
          ) : null}
          {noteTemplate?.clientInformation ? (
            <ClientInfoDisplay
              currentNoteTemplate={noteTemplate}
              patient={patient}
              staff={staff}
              diagnoses={diagnoses}
              careTeam={careTeam}
            />
          ) : null}
          {noteTemplate?.sessionInformation ? (
            <FormProvider {...methods}>
              <SessionInfoDisplay
                cptCodes={billingCodes}
                currentNoteTemplate={noteTemplate}
                appointment={appointment}
                session={session}
                staff={staff}
                startDate={'editedStartTimestamp'}
                endDate={'editedEndTimestamp'}
              />
            </FormProvider>
          ) : null}
          {noteTemplate?.clinicalNote ? (
            <>
              <Text style={[Typography.H5, styles.marginLVertical]}>
                {translations('note')}
              </Text>
              <View
                style={[
                  styles.cardFlat,
                  styles.row,
                  styles.justifySpaceBetween,
                  styles.padding,
                  styles.marginTop,
                ]}>
                <View style={[styles.paddingTop, styles.width]}>
                  <FormProvider {...methods}>
                    <Text style={[Typography.P3, styles.paddingBottom]}>
                      {watchedNote}
                    </Text>
                    {session.showAuthorsAndTimestamps ? (
                      <FlatList
                        keyExtractor={item => item.id}
                        data={notes}
                        renderItem={({item}) => (
                          <View style={[styles.paddingSMVertical]}>
                            <SessionNoteListItem
                              item={item}
                              canEdit={false}
                              showAuthorsAndTimestamps={true}
                            />
                          </View>
                        )}
                      />
                    ) : (
                      <Text style={[Typography.P3, styles.paddingBottom]}>
                        {concattedNotes}
                      </Text>
                    )}
                  </FormProvider>
                </View>
              </View>
            </>
          ) : null}
          <View
            style={[
              styles.row,
              styles.justifySpaceBetween,
              styles.alignCenter,
              styles.marginHorizontal,
              styles.marginLVertical,
            ]}
          />
          <View style={[styles.marginLVertical]}>
            {noteTemplate?.sessionData ? (
              <>
                <Text style={[Typography.H5]}>{translations('data')}</Text>
                <View style={[styles.marginLVertical]}>
                  <SessionSummaryDataTable
                    session={
                      session.type === 'supervision'
                        ? supervisedSession
                        : session
                    }
                    filters={{
                      types: [
                        noteTemplate?.skill ? 'skill' : '',
                        noteTemplate?.behavior ? 'behavior' : '',
                      ],
                      programs: [],
                    }}
                  />
                </View>
              </>
            ) : null}
          </View>

          {noteTemplate.additionalFields ? (
            <View style={[styles.marginLVertical]}>
              <>
                <Text style={[Typography.H5]}>
                  {translations('additional_fields')}
                </Text>
                <View
                  style={[
                    styles.cardFlat,
                    styles.padding,
                    styles.marginLVertical,
                  ]}>
                  <RenderFormInputs
                    values={noteTemplateValue?.formBuilderValues}
                    mode={'output'}
                    outputValues={session?.additionalFields}
                  />
                </View>
              </>
            </View>
          ) : (
            <></>
          )}
          {session?.amendedAt > 0 && !isSmallSize ? (
            <>
              <Text style={[Typography.H5]}>Modified Data</Text>
              <View
                style={[
                  styles.marginLVertical,
                  styles.padding,
                  styles.borderRadius,
                  styles.backgroundColorWhite,
                ]}>
                <TouchableOpacity
                  onPress={() => setShowModifiedData(!showModifiedData)}
                  style={[
                    styles.row,
                    styles.justifySpaceBetween,
                    styles.alignCenter,
                  ]}>
                  <View>
                    <Text style={[Typography.LABEL_MEDIUM]}>
                      This session's data has been modified
                    </Text>
                  </View>
                  <IconButton
                    color={Colors.RAVEN_BLACK}
                    icon={showModifiedData ? 'chevron-up' : 'chevron-down'}
                    onPress={() => setShowModifiedData(!showModifiedData)}
                  />
                </TouchableOpacity>
                <Collapsible
                  collapsed={!showModifiedData}
                  renderChildrenCollapsed={true}>
                  <View style={[styles.row, styles.paddingSMVertical]}>
                    <View style={[styles.flex, styles.paddingRight]}>
                      <Text style={Typography.P3_BOLD}>Edited By</Text>
                    </View>
                    <View style={[styles.flex, styles.paddingRight]}>
                      <Text style={Typography.P3_BOLD}>Edited At</Text>
                    </View>
                    <View style={[styles.flex, styles.paddingRight]}>
                      <Text style={Typography.P3_BOLD}>Program</Text>
                    </View>
                    <View style={[styles.flex, styles.paddingRight]}>
                      <Text style={Typography.P3_BOLD}>Target</Text>
                    </View>
                    {VISIBLE_FIELDS.map(fieldKey => (
                      <View
                        key={fieldKey}
                        style={[styles.flex, styles.paddingRight]}>
                        <Text style={Typography.P3_BOLD}>
                          {friendlyFieldName(fieldKey)}
                        </Text>
                      </View>
                    ))}
                  </View>
                  {loading || !auditLogs.length ? (
                    <ActivityIndicator />
                  ) : (
                    <FlatList
                      data={auditLogs}
                      keyExtractor={item => item?.data?.id}
                      renderItem={({item}: any) => (
                        <AmendedDataTableRow item={item} />
                      )}
                    />
                  )}
                </Collapsible>
              </View>
            </>
          ) : null}

          <Text style={[Typography.H5, styles.marginLVertical]}>
            {isSupervision ? 'Signature' : 'Signatures'}
          </Text>
          <View
            style={[
              styles.marginLVertical,
              styles.padding,
              styles.borderRadius,
              styles.backgroundColorWhite,
            ]}>
            {isSupervision ? (
              <>
                {session?.supervisionSignature ? (
                  <SignatureDisplay
                    value={{signature: session?.supervisionSignature}}
                    label={'Supervision Signature'}
                  />
                ) : (
                  <View style={[styles.container]}>
                    <View style={[styles.flex, styles.marginAutoRight]}>
                      {sessionSignatures.map((signature: any) => {
                        return <SignatureDisplay value={signature} />;
                      })}
                    </View>
                  </View>
                )}
              </>
            ) : (
              <>
                {session?.therapistSignature ? (
                  <>
                    <View style={[styles.container]}>
                      <View style={[styles.flex, styles.marginAutoRight]}>
                        <SignatureDisplay
                          value={{signature: session?.therapistSignature}}
                          label={'Therapist Signature'}
                        />
                      </View>
                      <View style={[styles.flex, styles.marginAutoRight]}>
                        <SignatureDisplay
                          value={{signature: session?.therapistSignature2}}
                          label={'Second Therapist Signature'}
                        />
                      </View>
                    </View>
                    <View style={[styles.container]}>
                      <View style={[styles.flex, styles.marginAutoRight]}>
                        <SignatureDisplay
                          value={{signature: session?.parentSignature1}}
                          label={'Parent Signature'}
                        />
                      </View>
                      <View style={[styles.flex, styles.marginAutoRight]}>
                        <SignatureDisplay
                          value={{signature: session?.parentSignature2}}
                          label={'Second Parent Signature'}
                        />
                      </View>
                    </View>
                  </>
                ) : (
                  <View style={[styles.container]}>
                    <View style={[styles.flex, styles.marginAutoRight]}>
                      {sessionSignatures.map((signature: any) => {
                        return <SignatureDisplay value={signature} />;
                      })}
                    </View>
                  </View>
                )}
              </>
            )}
          </View>
        </ViewShot>
      )}
    </View>
  );
};

export default compose(
  withDatabase,
  withState,
  withObservables([], ({session, database, profile}: any) => ({
    appointment: session.appointment,
    patient: session.patient,
    signatures: session.sessionSignatures,
    editor: session.noteEditedBy
      ? database.get(User.table).findAndObserve(session.noteEditedBy)
      : database.get(User.table).findAndObserve(profile.id),
    notes: session.activeNotes,
    billingCodes: session
      .observe()
      .pipe(map(currentSession => currentSession.cpt || [])),
    supervisedSessions:
      session.type === 'supervision'
        ? database
            .get(Session.table)
            .query(
              Q.where('type', 'session'),
              Q.where('appointment_id', session.appointmentId),
              Q.where('session_date', session.sessionDate),
              Q.where('deleted_at', null),
            )
        : of([]),
    role: profile?.role,
  })),
  withObservables([], ({appointment, supervisedSessions}: any) => ({
    staff: appointment.allStaff,
    supervisedSession: supervisedSessions.length
      ? supervisedSessions[0]
      : of({}),
  })),

  withObservables([], ({patient}: any) => {
    if (patient.id) {
      return {
        patientDiagnoses: patient.activeDiagnoses,
      };
    }
    return {
      patientDiagnoses: of([]),
    };
  }),
  withObservables([], ({database, patientDiagnoses}: any) => {
    return {
      diagnoses: database
        .get(InstanceDiagnosis.table)
        .query(
          Q.where(
            'id',
            Q.oneOf(
              patientDiagnoses.map(diagnosis => diagnosis.instanceDiagnosisId),
            ),
          ),
        ),
    };
  }),
  withObservables(['session'], ({database, session}: any) => ({
    noteTemplate: session.noteTemplateVersion,
    noteTemplateValue: database
      .get(NoteTemplateVersion.table)
      .findAndObserve(session.noteTemplateVersionId),
  })),
  withObservables(['authentication'], ({authentication, database}: any) => ({
    organization: database
      ?.get(Instance.table)
      .query(Q.where('_partition', authentication.selectedGroup))
      .observe()
      .pipe(mergeMap(x => x)),
  })),
  withObservables([], ({patient}: any) => ({
    careTeam: patient.careTeam,
  })),
)(SessionRecap);
